import {
  Input,
  InputGroup,
  InputGroupProps,
  InputLeftAddon,
  Select,
} from '@cardboard-ui/react';
import React from 'react';
import { usePhoneInput } from 'react-international-phone';
import { guessCountryCode } from 'utils/guessCountryCode';
import { useCountryListWithTranslation } from 'hooks/useCountryWithTranslation';
import { useI18n } from 'utils/i18n';
import { t } from '@lingui/macro';

interface PhoneNumberInputProps extends InputGroupProps {
  value: string;
  handleChange: (value: string) => void;
  isDisabled?: boolean;
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  size,
  value,
  handleChange,
  placeholder,
  isDisabled,
  ...rest
}) => {
  return (
    <InputGroup size={size} {...rest}>
      <ChakraPhoneInput
        onChange={handleChange}
        value={value}
        isDisabled={isDisabled}
      />
    </InputGroup>
  );
};

interface ChakraPhoneProps {
  value: string;
  onChange: (phone: string) => void;
  isDisabled?: boolean;
}

const ChakraPhoneInput: React.FC<ChakraPhoneProps> = ({
  value,
  onChange,
  isDisabled = false,
}) => {
  const { locale } = useI18n();
  const { getCountries } = useCountryListWithTranslation({ locale });
  const phoneInput = usePhoneInput({
    defaultCountry: guessCountryCode()?.toLowerCase() || undefined,
    value,
    onChange: (data) => {
      onChange(data.phone);
    },
  });
  return (
    <InputGroup>
      <InputLeftAddon pr={0}>
        <Select
          data-qa="phone-number-country"
          value={phoneInput.country}
          onChange={(e) => {
            phoneInput.setCountry(e.target.value);
          }}
          isDisabled={isDisabled}
          width="56px"
          variant="unstyled"
        >
          <option value=""></option>
          {getCountries().map((option) => (
            <option key={option.code} value={option.code.toLowerCase()}>
              <em-emoji id={`flag-${option.code.toLowerCase()}`} />{' '}
              {option.name}
            </option>
          ))}
        </Select>
      </InputLeftAddon>
      <Input
        data-qa="phone-number"
        placeholder={t`Phone number`}
        type="tel"
        color="primary"
        value={phoneInput.phone}
        onChange={phoneInput.handlePhoneValueChange}
        ref={phoneInput.inputRef}
        isDisabled={isDisabled}
      />
    </InputGroup>
  );
};
